import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface Profile {
  id: string;
  public_id: string | null;
  full_name: string | null;
  avatar_url: string | null;
  created_at: string;
  updated_at: string;
  questionnaire_completed: boolean | null;
  onboarding_completed_at: string | null;
  welcome_notice_seen_at: string | null;
  phone: string | null;
  phone_hash: string | null;
  phone_last4: string | null;
  phone_consent_at: string | null;
  phone_verified: boolean | null;
  preferred_language: string | null;
  deleted_at: string | null;
  last_activity_at: string | null;
  is_founding_member: boolean;
  founding_number: number | null;
  founding_status: string | null;
}

export function useProfile() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["profile", user?.id],
    queryFn: async (): Promise<Profile | null> => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("profiles")
        .select("id, public_id, full_name, avatar_url, created_at, updated_at, questionnaire_completed, onboarding_completed_at, welcome_notice_seen_at, phone, phone_hash, phone_last4, phone_consent_at, phone_verified, preferred_language, deleted_at, last_activity_at, is_founding_member, founding_number, founding_status")
        .eq("id", user.id)
        .maybeSingle();

      if (error) throw error;
      
      // If no profile exists, return a minimal profile object
      // This can happen if the trigger fails during signup
      if (!data) {
        console.warn("Profile not found for user, returning minimal profile");
        return {
          id: user.id,
          public_id: null,
          full_name: user.user_metadata?.full_name || null,
          avatar_url: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          questionnaire_completed: null,
          onboarding_completed_at: null,
          welcome_notice_seen_at: null,
          phone: null,
          phone_hash: null,
          phone_last4: null,
          phone_consent_at: null,
          phone_verified: null,
          preferred_language: null,
          deleted_at: null,
          last_activity_at: null,
          is_founding_member: false,
          founding_number: null,
          founding_status: null,
        };
      }
      
      return data;
    },
    enabled: !!user?.id,
  });
}
